import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, CircularProgress, Button, ButtonGroup,
  List, ListItem, ListItemText, ListItemSecondaryAction, Avatar, Divider,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SavingsIcon from '@mui/icons-material/Savings';
import PercentIcon from '@mui/icons-material/Percent';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useBranch } from '../context/BranchContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { CLP } from '../utils/finance';
import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const STATUS_CARDS = {
  pending: { label: 'Pendiente', color: '#D97706', bg: '#FEF3C7', icon: <PendingActionsIcon sx={{ fontSize: 28 }} />, filter: '' },
  processing: { label: 'Procesando', color: '#2563EB', bg: '#DBEAFE', icon: <HourglassTopIcon sx={{ fontSize: 28 }} />, filter: 'processing' },
  reported: { label: 'Informado', color: '#059669', bg: '#D1FAE5', icon: <ScienceIcon sx={{ fontSize: 28 }} />, filter: 'reported' },
};

const STATUS_CONFIG = {
  solicitado:     { label: 'Solicitado', color: '#6366F1', bg: '#EDE9FE' },
  pending_upload: { label: 'Pendiente',  color: '#D97706', bg: '#FEF3C7' },
  processing:     { label: 'Procesando', color: '#2563EB', bg: '#DBEAFE' },
  pending_review: { label: 'En revisión', color: '#CA8A04', bg: '#FEF9C3' },
  approved:       { label: 'Aprobado',   color: '#16A34A', bg: '#DCFCE7' },
  reported:       { label: 'Informado',  color: '#059669', bg: '#D1FAE5' },
};

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const RANGE_OPTIONS = [
  { key: 7, label: '7 días' },
  { key: 30, label: '30 días' },
  { key: 90, label: '90 días' },
];

function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function KpiCard({ title, value, subtitle, icon, color, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1.5px solid ${color}20`,
        bgcolor: `${color}08`,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 3 } : undefined,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 40, height: 40 }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{title}</Typography>
          <Typography variant="h5" fontWeight={800} color={color} lineHeight={1.1}>{value}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}

function fmtHours(h) {
  if (h == null || Number.isNaN(h)) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${mm}m`;
}

export default function LabDashboard() {
  const navigate = useNavigate();
  const { filterByBranch } = useBranch();
  const [orders, setOrders] = useState([]);
  const [results, setResults] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [rangeDays, setRangeDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubOrders = onSnapshot(
      query(collection(db, 'labOrders'), orderBy('createdAt', 'desc')),
      (snap) => setOrders(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }))
    );
    const unsubResults = onSnapshot(
      query(collection(db, 'labResults'), orderBy('uploadedAt', 'desc')),
      (snap) => {
        setResults(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
        setLoading(false);
      }
    );
    let mounted = true;
    const loadStatic = async () => {
      const [etSnap, auditSnap] = await Promise.all([
        getDocs(query(collection(db, 'examTypes'), orderBy('name'))),
        getDocs(query(collection(db, 'labAuditLog'), where('action', '==', 'result_rejected'))),
      ]);
      if (!mounted) return;
      setExamTypes(etSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAuditLog(auditSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    loadStatic().catch(() => {});
    return () => {
      unsubOrders();
      unsubResults();
      mounted = false;
    };
  }, [filterByBranch]);

  const startDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - rangeDays + 1);
    return d;
  }, [rangeDays]);

  const endDate = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const inRange = (v) => {
    const d = toDate(v);
    return d && d >= startDate && d <= endDate;
  };

  const periodOrders = useMemo(
    () => orders.filter((o) => inRange(o.createdAt)),
    [orders, startDate, endDate]
  );

  const periodResults = useMemo(
    () => results.filter((r) => inRange(r.reportGeneratedAt || r.uploadedAt)),
    [results, startDate, endDate]
  );

  const examTypesMap = useMemo(() => {
    const map = {};
    examTypes.forEach((e) => { map[e.id] = e; });
    return map;
  }, [examTypes]);

  const totalEmitted = periodOrders.length;
  const paidOrders = useMemo(
    () => periodOrders.filter((o) => o.paymentStatus === 'pagado'),
    [periodOrders]
  );
  const paidProcessed = useMemo(
    () => paidOrders.filter((o) => o.status === 'reported' || o.status === 'approved'),
    [paidOrders]
  );
  const conversionRate = totalEmitted > 0 ? (paidProcessed.length / totalEmitted) * 100 : 0;

  const emissionStats = useMemo(() => {
    const orderMap = new Map(orders.map((o) => [o.id, o]));
    const values = [];
    const byExam = {};
    periodResults.forEach((r) => {
      if (!r.reportGeneratedAt) return;
      const order = orderMap.get(r.orderId);
      const start = order ? toDate(order.createdAt) : toDate(r.createdAt);
      const end = toDate(r.reportGeneratedAt);
      if (!start || !end || end <= start) return;
      const hours = (end - start) / 36e5;
      values.push(hours);
      const name = r.examTypeName || order?.examsRequested?.[0]?.examTypeName || '—';
      if (!byExam[name]) byExam[name] = [];
      byExam[name].push(hours);
    });
    if (values.length === 0) return { avg: 0, median: 0, p90: 0, count: 0, byExam: [] };
    values.sort((a, b) => a - b);
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    const median = values[Math.floor(values.length / 2)];
    const p90 = values[Math.floor((values.length - 1) * 0.9)];
    const byExamArray = Object.entries(byExam)
      .map(([name, vals]) => ({
        name,
        avg: vals.reduce((s, v) => s + v, 0) / vals.length,
        count: vals.length,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    return { avg, median, p90, count: values.length, byExam: byExamArray };
  }, [orders, periodResults]);

  const reprocessStats = useMemo(() => {
    const rejected = new Set(
      auditLog
        .filter((l) => inRange(l.performedAt))
        .map((l) => l.resultId)
        .filter(Boolean)
    );
    const total = periodResults.length;
    return {
      total,
      rejected: rejected.size,
      rate: total > 0 ? (rejected.size / total) * 100 : 0,
    };
  }, [auditLog, periodResults, startDate, endDate]);

  const financialStats = useMemo(() => {
    let totalSales = 0;
    let totalCost = 0;
    const byType = {};
    paidOrders.forEach((o) => {
      (o.examsRequested || []).forEach((e) => {
        const price = Number(e.precioArancel) || Math.round((Number(e.precioNeto) || 0) * 1.19);
        const exam = examTypesMap[e.examTypeId];
        const cost = (exam?.insumos || []).reduce((s, i) => s + (parseFloat(i.costo) || 0), 0);
        totalSales += price;
        totalCost += cost;
        const name = e.examTypeName || '—';
        if (!byType[name]) byType[name] = { name, sales: 0, cost: 0, count: 0 };
        byType[name].sales += price;
        byType[name].cost += cost;
        byType[name].count += 1;
      });
    });
    return {
      totalSales,
      totalCost,
      margin: totalSales > 0 ? ((totalSales - totalCost) / totalSales) * 100 : 0,
      byType: Object.values(byType).sort((a, b) => b.sales - a.sales),
    };
  }, [paidOrders, examTypesMap]);

  const statusCardsData = useMemo(() => {
    const paidNotReported = orders.filter((o) => o.paymentStatus === 'pagado' && o.status !== 'reported' && o.status !== 'approved');
    const processing = orders.filter((o) => o.status === 'processing');
    const reported = orders.filter((o) => o.status === 'reported');
    return {
      pending: {
        count: paidNotReported.length,
        exams: paidNotReported.reduce((s, o) => s + (o.examsRequested || []).length, 0),
      },
      processing: {
        count: processing.length,
        exams: processing.reduce((s, o) => s + (o.examsRequested || []).length, 0),
      },
      reported: {
        count: reported.length,
        exams: reported.reduce((s, o) => s + (o.examsRequested || []).length, 0),
      },
    };
  }, [orders]);

  const todayOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders
      .filter((o) => {
        const d = toDate(o.createdAt);
        return d && d >= today;
      })
      .sort((a) => (a.priority === 'urgent' ? -1 : 1));
  }, [orders]);

  const criticalAlerts = useMemo(() => {
    return results
      .filter((r) => r.extractedParameters?.some((p) => p.status === 'critical'))
      .slice(0, 5);
  }, [results]);

  const weekChartData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (6 - i));
      d.setHours(0, 0, 0, 0);
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      const dayOrders = orders.filter((o) => {
        const ts = toDate(o.createdAt);
        return ts && ts >= d && ts < next;
      });
      return {
        day: DAY_NAMES[d.getDay()],
        Emitidas: dayOrders.length,
        Pagadas: dayOrders.filter((o) => o.paymentStatus === 'pagado' && (o.status === 'reported' || o.status === 'approved')).length,
      };
    });
  }, [orders]);

  const truncate = (s, n = 22) => (s && s.length > n ? s.slice(0, n) + '...' : s || '');

  const salesByExamChartData = useMemo(
    () => financialStats.byType.slice(0, 8).map((x) => ({ ...x, fullName: x.name, name: truncate(x.name) })),
    [financialStats]
  );

  const emissionByExamChartData = useMemo(
    () => emissionStats.byExam.slice(0, 8).map((x) => ({ ...x, fullName: x.name, name: truncate(x.name) })),
    [emissionStats]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: LAB_PRIMARY, width: 44, height: 44 }}>
            <ScienceIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Laboratorio Clínico</Typography>
            <Typography variant="caption" color="text.secondary">Dashboard de gestión</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ButtonGroup size="small" sx={{ mr: 1 }}>
            {RANGE_OPTIONS.map((opt) => (
              <Button
                key={opt.key}
                variant={rangeDays === opt.key ? 'contained' : 'outlined'}
                onClick={() => setRangeDays(opt.key)}
                sx={{ fontWeight: 600 }}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/lab/orders/new')}
            sx={{ borderColor: LAB_PRIMARY, color: LAB_PRIMARY, fontWeight: 600 }}>
            Nueva Orden
          </Button>
          <Button variant="contained" startIcon={<UploadFileIcon />}
            onClick={() => navigate('/lab/upload')}
            sx={{ bgcolor: LAB_TEAL, '&:hover': { bgcolor: '#229984' }, fontWeight: 600 }}>
            Subir Resultado
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Órdenes emitidas"
            value={totalEmitted}
            subtitle="en el período"
            icon={<TrendingUpIcon />}
            color={LAB_PRIMARY}
            onClick={() => navigate('/lab/orders')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Pagadas y procesadas"
            value={paidProcessed.length}
            subtitle={`de ${paidOrders.length} pagadas`}
            icon={<CheckCircleIcon />}
            color="#16A34A"
            onClick={() => navigate('/lab/orders?status=reported')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Conversión"
            value={`${conversionRate.toFixed(1)}%`}
            subtitle="emitidas → pagadas/procesadas"
            icon={<PercentIcon />}
            color="#7C3AED"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="TLE promedio"
            value={fmtHours(emissionStats.avg)}
            subtitle={`mediana ${fmtHours(emissionStats.median)}`}
            icon={<ScheduleIcon />}
            color="#2563EB"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Tasa de reproceso"
            value={`${reprocessStats.rate.toFixed(1)}%`}
            subtitle={`${reprocessStats.rejected} de ${reprocessStats.total} resultados`}
            icon={<ErrorOutlineIcon />}
            color="#DC2626"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Ventas totales"
            value={CLP(financialStats.totalSales)}
            subtitle="exámenes pagados"
            icon={<AttachMoneyIcon />}
            color="#059669"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Costo estimado"
            value={CLP(financialStats.totalCost)}
            subtitle="insumos registrados"
            icon={<SavingsIcon />}
            color="#C2410C"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <KpiCard
            title="Margen bruto"
            value={`${financialStats.margin.toFixed(1)}%`}
            subtitle={CLP(financialStats.totalSales - financialStats.totalCost)}
            icon={<TrendingUpIcon />}
            color={financialStats.margin >= 30 ? '#16A34A' : financialStats.margin >= 15 ? '#D97706' : '#DC2626'}
          />
        </Grid>
      </Grid>

      {/* Status cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(STATUS_CARDS).map(([status, cfg]) => (
          <Grid
            key={status}
            size={{ xs: 6, sm: 4, md: 4 }}
          >
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1.5px solid ${cfg.color}20`, bgcolor: cfg.bg, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate(cfg.filter ? `/lab/orders?status=${cfg.filter}` : '/lab/orders')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color={cfg.color} lineHeight={1}>{statusCardsData[status].count}</Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{cfg.label}</Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>{statusCardsData[status].exams} exámenes</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Weekly activity */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              Actividad de la semana
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekChartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Emitidas" fill={LAB_TEAL} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Pagadas" fill="#16A34A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Sales by exam type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              Ventas por tipo de examen ({salesByExamChartData.length})
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={salesByExamChartData}
                layout="vertical"
                margin={{ top: 4, right: 24, bottom: 0, left: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => CLP(v)} labelFormatter={(_, p) => p?.[0]?.payload?.fullName || p?.[0]?.payload?.name || ''} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" name="Ventas" fill={LAB_TEAL} radius={[0, 4, 4, 0]} />
                <Bar dataKey="cost" name="Costos" fill="#DC2626" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Emission time by exam */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              Tiempo de emisión por examen (promedio)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={emissionByExamChartData}
                layout="vertical"
                margin={{ top: 4, right: 24, bottom: 0, left: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}h`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtHours(v)} labelFormatter={(_, p) => p?.[0]?.payload?.fullName || p?.[0]?.payload?.name || ''} />
                <Bar dataKey="avg" name="Horas promedio" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Today's orders */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={1}>
              Órdenes de hoy ({todayOrders.length})
            </Typography>
            {todayOrders.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>Sin órdenes hoy.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {todayOrders.map((o, i) => (
                  <React.Fragment key={o.id}>
                    {i > 0 && <Divider />}
                    <ListItem
                      button
                      onClick={() => navigate(`/lab/orders/${o.id}`)}
                      sx={{ borderRadius: 1, '&:hover': { bgcolor: '#F0FDF4' } }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {o.priority === 'urgent' && (
                              <Chip label="URGENTE" size="small" sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                            )}
                            <Typography variant="body2" fontWeight={600} noWrap>{o.patientName || 'Paciente'}</Typography>
                          </Box>
                        }
                        secondary={`${o.examsRequested?.map((e) => e.examTypeName).join(', ') || '—'}`}
                        secondaryTypographyProps={{ noWrap: true, fontSize: '0.72rem' }}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={STATUS_CONFIG[o.status]?.label || o.status}
                          size="small"
                          sx={{ fontSize: '0.65rem', bgcolor: STATUS_CONFIG[o.status]?.bg, color: STATUS_CONFIG[o.status]?.color, fontWeight: 600 }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Critical alerts */}
        {criticalAlerts.length > 0 && (
          <Grid size={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1.5px solid #FECACA', bgcolor: '#FFF5F5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <WarningAmberIcon sx={{ color: '#DC2626' }} />
                <Typography variant="subtitle1" fontWeight={700} color="#DC2626">
                  Alertas — Valores Críticos ({criticalAlerts.length})
                </Typography>
              </Box>
              {criticalAlerts.map((r) => {
                const critParams = r.extractedParameters?.filter((p) => p.status === 'critical') || [];
                return (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1, bgcolor: '#FEE2E2', borderRadius: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="#991B1B">{r.patientName}</Typography>
                      <Typography variant="caption" color="#DC2626">
                        {critParams.map((p) => `${p.paramName}: ${p.value} ${p.unit}`).join(' | ')}
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" sx={{ borderColor: '#DC2626', color: '#DC2626', fontSize: '0.7rem' }}
                      onClick={() => navigate(`/lab/review/${r.id}`)}>
                      Revisar
                    </Button>
                  </Box>
                );
              })}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
